import React, { type KeyboardEvent, type MouseEvent, useState } from 'react';

import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import LinkItem from '@atlaskit/menu/link-item';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import type { AvailableSite } from '../../types/site';

type SwitchSiteItemProps = {
	onItemClick: (e: MouseEvent | KeyboardEvent, site: AvailableSite) => void;
	site: AvailableSite;
	isSelected: boolean;
	href: string;
};

// MenuGroup max width of 480 - 56 (icon width + padding + margin)
const MAX_WIDTH_OF_TEXT_BEFORE_TRUNCATING = 424;

/**
 * Duplicated from townsquare's `SwitchWorkspaceItem`. Renders a single
 * site row inside the SiteSwitcher dropdown, with an office-building
 * icon, selection state, and an ellipsis-tooltip when the displayName
 * overflows the menu's max content width.
 */
export const SwitchSiteItem = ({
	onItemClick,
	site,
	href,
	isSelected,
}: SwitchSiteItemProps): JSX.Element => {
	const [includeTooltip, setIncludeTooltip] = useState(false);
	const handleOnClick = (e: MouseEvent | KeyboardEvent): void => onItemClick(e, site);

	const SiteLinkItem = (
		<div>
			<LinkItem
				href={href}
				iconBefore={<OfficeBuildingIcon color="currentColor" spacing="spacious" label="" />}
				key={`site-switcher-option-${site.cloudId ?? site.url}`}
				testId={
					site.cloudId ? `site-switcher-option-${site.cloudId}` : 'site-switcher-option-unknown'
				}
				onClick={handleOnClick}
				isSelected={isSelected}
			>
				<span
					ref={(el) => {
						if ((el?.offsetWidth ?? 0) >= MAX_WIDTH_OF_TEXT_BEFORE_TRUNCATING) {
							setIncludeTooltip(true);
						}
					}}
				>
					{site.displayName}
				</span>
			</LinkItem>
		</div>
	);

	return includeTooltip ? (
		<Tooltip key={site.cloudId ?? site.url} content={site.displayName}>
			{SiteLinkItem}
		</Tooltip>
	) : (
		SiteLinkItem
	);
};
